import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
}

const ROLE_COLOR: Record<string, string> = {
  super_admin:  'var(--green)',
  tenant_admin: 'var(--green)',
  manager:      'var(--amber)',
  viewer:       'var(--gray)',
};
const ROLE_BG: Record<string, string> = {
  super_admin:  'var(--green-bg)',
  tenant_admin: 'var(--green-bg)',
  manager:      'var(--amber-bg)',
  viewer:       '#f5f5f3',
};

export default function UsersPage({ userRole }: { userRole: string }) {
  const { t, i18n } = useTranslation();
  const [users, setUsers]       = useState<User[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError]       = useState('');

  const isAdmin = userRole === 'super_admin' || userRole === 'tenant_admin';

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users');
      setUsers(data);
    } catch {
      setError(t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDeactivate = async (id: string) => {
    if (!confirm(t('users.confirmDeactivate'))) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const handleRoleChange = async (id: string, role: string) => {
    await api.put(`/users/${id}`, { role });
    load();
  };

  const columns = [
    t('users.columns.user'),
    t('users.columns.role'),
    t('users.columns.status'),
    t('users.columns.lastAccess'),
    ...(isAdmin ? [t('users.columns.actions')] : []),
  ];

  return (
    <div className="page">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('users.title')}</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)' }}>{t('users.count', { count: users.length })}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowForm(true)}
            style={{ padding: '8px 18px', background: 'var(--dark)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
          >
            {t('users.newUser')}
          </button>
        )}
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13 }}>{t('users.loading')}</div>
      ) : (
        <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                {columns.map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{u.name || u.email}</div>
                    {u.name && <div style={{ fontSize: 11, color: 'var(--gray)' }}>{u.email}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {isAdmin && u.role !== 'super_admin' ? (
                      <select
                        value={u.role}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, background: ROLE_BG[u.role], color: ROLE_COLOR[u.role], fontWeight: 600 }}
                      >
                        <option value="tenant_admin">{t('users.roles.tenant_admin')}</option>
                        <option value="manager">{t('users.roles.manager')}</option>
                        <option value="viewer">{t('users.roles.viewer')}</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: ROLE_BG[u.role], color: ROLE_COLOR[u.role], fontWeight: 600 }}>
                        {t(`users.roles.${u.role}`, u.role)}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: u.isActive ? 'var(--green-bg)' : '#f5f5f3', color: u.isActive ? 'var(--green-dk)' : 'var(--gray)', fontWeight: 600 }}>
                      {u.isActive ? t('status.active') : t('status.inactive')}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray)' }}>
                    {u.lastLogin
                      ? new Date(u.lastLogin).toLocaleDateString(i18n.language.startsWith('es') ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                      : t('users.lastLogin.never')}
                  </td>
                  {isAdmin && (
                    <td style={{ padding: '12px 16px' }}>
                      {u.role !== 'super_admin' && u.isActive && (
                        <button
                          onClick={() => handleDeactivate(u.id)}
                          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
                        >
                          {t('users.deactivate')}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <UserFormModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function UserFormModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'viewer' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/users', form);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error ?? t('users.form.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 20 }}>{t('users.form.title')}</h2>
        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>{t('users.form.name')}</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('users.form.namePlaceholder')} />
          </div>
          <div className="field">
            <label>{t('users.form.email')}</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder={t('users.form.emailPlaceholder')} />
          </div>
          <div className="field">
            <label>{t('users.form.tempPassword')}</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required placeholder={t('users.form.passwordPlaceholder')} minLength={8} />
          </div>
          <div className="field">
            <label>{t('users.form.role')}</label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ width: '100%', padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none' }}>
              <option value="tenant_admin">{t('users.roles.tenant_admin')}</option>
              <option value="manager">{t('users.roles.manager')}</option>
              <option value="viewer">{t('users.roles.viewer')}</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', fontSize: 13, cursor: 'pointer' }}>{t('users.form.cancel')}</button>
            <button type="submit" disabled={saving} className="primary-button" style={{ flex: 1 }}>{saving ? t('users.form.saving') : t('users.form.create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
